'use client';
import { useRef, useEffect, useCallback, KeyboardEvent, ClipboardEvent } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { cn } from '@/lib/utils';
interface OTPInputProps {
    value: string;
    onChange: (value: string) => void;
    onComplete?: (value: string) => void;
    length?: number;
    disabled?: boolean;
    error?: boolean;
    autoFocus?: boolean;
}
export function OTPInput({ value, onChange, onComplete, length = 6, disabled = false, error = false, autoFocus = true }: OTPInputProps) {
    const t = useTranslations$1();
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    // Focus first input on mount
    useEffect(()=>{
        if (autoFocus && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [
        autoFocus
    ]);
    // Focus the appropriate input when value changes externally
    useEffect(()=>{
        const nextIndex = Math.min(value.length, length - 1);
        if (inputRefs.current[nextIndex]) {
            inputRefs.current[nextIndex]?.focus();
        }
    }, [
        value.length,
        length
    ]);
    const handleChange = useCallback((index: number, digit: string)=>{
        // Only allow digits
        if (digit && !/^\d$/.test(digit)) return;
        const newValue = value.split('');
        newValue[index] = digit;
        const result = newValue.join('').slice(0, length);
        onChange(result);
        // Auto-submit when all digits are filled
        if (result.length === length) {
            onComplete?.(result);
        } else if (digit && index < length - 1) {
            // Auto-advance to next input
            inputRefs.current[index + 1]?.focus();
        }
    }, [
        value,
        length,
        onChange,
        onComplete
    ]);
    const handleKeyDown = useCallback((index: number, e: KeyboardEvent<HTMLInputElement>)=>{
        if (e.key === 'Backspace') {
            e.preventDefault();
            if (value[index]) {
                // Clear current digit
                const newValue = value.split('');
                newValue[index] = '';
                onChange(newValue.join(''));
            } else if (index > 0) {
                // Move to previous input and clear it
                inputRefs.current[index - 1]?.focus();
                const newValue = value.split('');
                newValue[index - 1] = '';
                onChange(newValue.join(''));
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < length - 1) {
            e.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
    }, [
        value,
        length,
        onChange
    ]);
    const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>)=>{
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
        if (pastedData) {
            onChange(pastedData);
            if (pastedData.length === length) {
                onComplete?.(pastedData);
            } else {
                // Focus the last filled input or the next empty one
                const focusIndex = Math.min(pastedData.length, length - 1);
                inputRefs.current[focusIndex]?.focus();
            }
        }
    }, [
        length,
        onChange,
        onComplete
    ]);
    return (<div className="flex gap-2 justify-center">
      {Array.from({
        length
    }).map((_, index)=>(<input key={index} ref={(el)=>{
            inputRefs.current[index] = el;
        }} type="text" inputMode="numeric" pattern="\d*" maxLength={1} value={value[index] || ''} onChange={(e)=>handleChange(index, e.target.value.slice(-1))} onKeyDown={(e)=>handleKeyDown(index, e)} onPaste={handlePaste} disabled={disabled} className={cn('w-11 h-14 text-center text-2xl font-semibold rounded-lg border bg-background transition-all', 'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background', 'disabled:cursor-not-allowed disabled:opacity-50', error ? 'border-destructive focus:ring-destructive' : 'border-input')} aria-label={t("-gaqnB", {
            value: String(index + 1),
            length: String(length)
        })}/>))}
    </div>);
}
